import { Button } from '@mui/material';
import { DataGridPremium, GridColDef, GridRowModel, itIT } from '@mui/x-data-grid-premium';
import { EnhancedAccordion } from 'components/enhanced/accordion/EnhancedAccordion';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { required } from 'components/helpers/validators';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { equipmentService, examService, productService, workstationService } from 'shared/services';
import { Option, StatusOption } from 'shared/types/general';
import { v4 as uuidv4 } from 'uuid';
import { useWorkstationLinkColumns } from '../hooks/useWorkstationLinkColumns';
import { Workstation, WorkstationLink } from '../types';
import { sectorService } from 'features/settings/topology/api/sector.service';
import { IdentityField, AutocompleteAdapter, TextFieldAdapter } from 'components/enhanced';

interface WorkstationFormParams {
  workstation: Workstation;
  onSubmit: any;
  disableEdit?: boolean;
}

export const WorkstationForm = ({ workstation, onSubmit, disableEdit }: WorkstationFormParams) => {
  const translationPrefix = 'entities.workstation';
  const { t } = useTranslation();

  const [sectors, setSectors] = useState<Option[]>([]);
  const [products, setProducts] = useState<Option[]>([]);
  const [equipments, setEquipments] = useState<Option[]>([]);
  const [workstations, setWorkstations] = useState<Option[]>([]);
  const [workstationLinks, setWorkstationLinks] = useState<WorkstationLink[]>([]);
  const [phaseOptions, setPhaseOptions] = useState<StatusOption[]>([]);
  const [exams, setExams] = useState<Option[]>([]);

  useEffect(() => {
    productService.getAllBaseInformation().then(setProducts);
    equipmentService.getAllBaseInformation().then(setEquipments);
    sectorService.getAllBaseInformation().then(setSectors);
    workstationService.getAllBaseInformation().then(setWorkstations);
    examService.getAllBaseInformation().then(setExams);
    workstationService.options().then((res) => {
      if (res.actions && res.actions.POST) {
        setPhaseOptions([
          { value: null, display_name: '' },
          ...res.actions.POST.phase.choices.map((item) => {
            return {
              ...item,
              display_name: t(`${translationPrefix}.phase_options.${item.display_name}`)
            };
          })
        ]);
      }
    });
  }, []);

  useEffect(() => {
    if (workstation?.workstations) setWorkstationLinks(workstation.workstations);
  }, [workstation]);

  const handleDelete = (line) => {
    setWorkstationLinks((prevState) =>
      _.filter(prevState, function (p) {
        return p.id !== line.id;
      })
    );
  };

  const handleProcessRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
    // Trova quale campo è cambiato (se ti serve sapere il field specifico)
    const changedField = Object.keys(newRow).find((key) => newRow[key] !== oldRow[key]) as string;

    setWorkstationLinks((prevState) =>
      prevState.map((p) => {
        if (p.id === newRow.id) {
          return {
            ...p,
            [changedField]: newRow[changedField]
          };
        }
        return p;
      })
    );

    // v6 richiede Promise con la riga aggiornata
    return Promise.resolve(newRow);
  };

  const onAddLink = (values) => {
    const linkToAdd = {
      id: uuidv4(),
      workstation_origin: workstation.id,
      destination_code: workstations.find((ws) => ws.id == values.workstation_destination).code,
      workstation_destination: values.workstation_destination,
      weight: values.weight ?? 1
    };
    setWorkstationLinks((prevState) => [...prevState, linkToAdd]);
  };

  const columns: GridColDef[] = useWorkstationLinkColumns(handleDelete);

  return (
    <div>
      {workstation && (
        <>
          <EnhancedForm
            onSubmit={(values) => {
              if (!disableEdit) {
                onSubmit({ ...values, workstations: workstationLinks });
              }
            }}
            edit={!!workstation.id}
            disableEdit={disableEdit}
            columns={2}
            autoStyleLayout={true}>
            <IdentityField
              gridSpan={2}
              initValue={workstation.sector}
              name="sector"
              component={AutocompleteAdapter}
              options={sectors}
              label="Sector"
              type="text"
              required
              disabled={disableEdit}
            />
            <IdentityField
              name="code"
              defaultValue={workstation.code}
              component={TextFieldAdapter}
              label={t('global.code')}
              type="text"
              disabled={disableEdit}
            />
            <IdentityField
              name="phase"
              label={t(`${translationPrefix}.phase`)}
              initValue={
                workstation.phase
                  ? {
                      ...workstation.phase,
                      display_name: t(
                        `${translationPrefix}.phase_options.${workstation.phase.display_name}`
                      )
                    }
                  : {
                      value: null,
                      display_name: ''
                    }
              }
              defaultValue={workstation.phase ? workstation.phase.value : 1}
              component={AutocompleteAdapter}
              options={phaseOptions}
              type="text"
              optionLabel="display_name"
              optionValue="value"
              disabled={disableEdit}
            />
            <IdentityField
              gridSpan={2}
              name="description"
              defaultValue={workstation.description}
              component={TextFieldAdapter}
              label={t('global.description')}
              type="text"
              disabled={disableEdit}
            />
          </EnhancedForm>
          <br />
          {!disableEdit && (
            <EnhancedAccordion
              title={t('global.non_productive_workstations')}
              subtitle={t('entities.workstation_link.add')}>
              <EnhancedForm
                onSubmit={onAddLink}
                formId="addWorkstationLink"
                costumControls={
                  <Button type="submit" form="addWorkstationLink" variant="outlined" fullWidth>
                    {t('actions.add')}
                  </Button>
                }
                autoStyleLayout={true}
                columns={4}>
                <IdentityField
                  gridSpan={3}
                  name="workstation_destination"
                  component={AutocompleteAdapter}
                  options={workstations}
                  label={t('global.workstation')}
                  validate={required}
                  required
                />
                <IdentityField
                  name="weight"
                  label={t(`global.weight`)}
                  component={TextFieldAdapter}
                  type="number"
                />
              </EnhancedForm>
            </EnhancedAccordion>
          )}
          <EnhancedCard>
            <DataGridPremium
              density="compact"
              rows={workstationLinks}
              localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
              sx={{ border: 'none' }}
              disableRowSelectionOnClick
              columns={columns}
              // Pagination v6 - oggetto unificato (page è 0-based!)
              paginationModel={{ page: 0, pageSize: 20 }}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              // Edit v6 - processRowUpdate al posto di onCellEditCommit
              processRowUpdate={handleProcessRowUpdate}
            />
          </EnhancedCard>
        </>
      )}
    </div>
  );
};
